import React, { useState } from "react";
import { DateInput, FormInput, FormLabel, NumberInput } from "../../../components/forms";
import { IconButton } from "../../../components";
import { useCustodianTransactionMatching } from "../hooks/CustodianTransactionMatchingContext";
import { ErrorMessageWrapper, ErrorMessage } from "../../../components/messages"
import Skeleton from "react-loading-skeleton";
import { ThemedSpan, Tooltip } from "../../../components/utilities";
import TransactionExceptionsResolveDropdown from "./TransactionExceptionsResolveDropdown";
import classNames from "classnames";
import { Card } from "react-bootstrap";
import { CardBody, CardFooter, CardHeader } from "../../../components/utilities/Card";
import styled from "styled-components";
import RetryMatchingModal from "./RetryMatchingModal";
import BulkResolveModal from "./BulkResolveModal";
import HubwiseTransactionsModal from "./HubwiseTransactionsModal";

const StyledCard = styled(Card)`
    height: 60%;
`;

const Label = ({ children }) => (
    <FormLabel className={"me-2"}>
        {children}
    </FormLabel>
);

const InputSkeleton = styled(Skeleton)`
    height: 32px;
`

const TransactionExceptions = () => {

    const [showBulkModal, setShowBulkModal] = useState(false);
    const [showRetryModal, setShowRetryModal] = useState(false);
    const [showHubwiseTransactionsModal, setShowHubwiseTransactionsModal] = useState(false);

    const {
        exception,
        isFetching,
        isError,
        error,
        totalCount,
        page,
        first,
        last,
        next,
        previous,
        retryFetchExceptions,
        setPage,
        setSearch
    } = useCustodianTransactionMatching();

    const {
        custodianCode,
        platformAccountId,
        sedol,
        assetName,
        platformTransactionId,
        tradeDate,
        platformTransactionType,
        quantity,
        transactionValue,
        wealthProStatusDescription,
        dateAdded,
        revisionMarker,
        platformClientName,
        platformAccountName,
        wealthProAccountReference
    } = exception || {};

    if (isError) {
        return (
            <ErrorMessageWrapper>
                <ErrorMessage error={error} retryCallback={retryFetchExceptions} />
            </ErrorMessageWrapper>
        );
    }

    var body = (
        <div className="row mx-0">
                        <div className="col-7">
                            <div className="row mb-3">
                                <div className="col-1 d-flex align-items-center">
                                    <FormLabel>Client</FormLabel>
                                </div>
                                <div className="col">
                                    {isFetching
                                        ? <InputSkeleton />
                                        : <FormInput
                                            readOnly
                                            value={platformClientName}
                                        />}
                                </div>
                                <div className="col-1 d-flex align-items-center">
                                    <FormLabel>Account</FormLabel>
                                </div>
                                <div className="col">
                                    {isFetching
                                        ? <InputSkeleton />
                                        : <FormInput
                                            readOnly
                                            value={platformAccountName}
                                        />}
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-1 d-flex align-items-center">
                                    <FormLabel>Account ID</FormLabel>
                                </div>
                                <div className="col">
                                    {isFetching
                                        ? <InputSkeleton />
                                        : <FormInput
                                            readOnly
                                            value={platformAccountId?.trim()}
                                        />}
                                </div> 
                                <div className="col-1 d-flex align-items-center">
                                    <FormLabel>Operation</FormLabel>
                                </div>
                                <div className="col">
                                    {isFetching
                                        ? <InputSkeleton />
                                        : <FormInput
                                            readOnly
                                            value={platformTransactionType}
                                        />}
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-1 d-flex align-items-center">
                                    <FormLabel>SEDOL</FormLabel>
                                </div>
                                <div className="col">
                                    {isFetching
                                        ? <InputSkeleton />
                                        : <FormInput
                                            readOnly
                                            value={sedol}
                                        />}
                                </div>
                                <div className="col-1 d-flex align-items-center">
                                    <FormLabel>Asset</FormLabel>
                                </div>
                                <div className="col">
                                    {isFetching
                                        ? <InputSkeleton />
                                        : <FormInput
                                            readOnly
                                            value={assetName}
                                        />}
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-1 d-flex align-items-center">
                                    <FormLabel>Deal Units</FormLabel>
                                </div>
                                <div className="col">
                                    {isFetching
                                        ? <InputSkeleton />
                                        : <NumberInput
                                            readOnly
                                            value={quantity}
                                        />}
                                </div>
                                <div className="col-1 d-flex align-items-center">
                                    <FormLabel>Deal Value</FormLabel>
                                </div>
                                <div className="col">
                                    {isFetching
                                        ? <InputSkeleton />
                                        : <NumberInput
                                            readOnly
                                            value={transactionValue}
                                        />}
                                </div>
                            </div>
                            <div className="row mb-3">
                                <div className="col-1 d-flex align-items-center">
                                    <FormLabel>Trade Date</FormLabel>
                                </div>
                                <div className="col">
                                    {isFetching
                                        ? <InputSkeleton />
                                        : <DateInput
                                            readOnly
                                            ButtonComponent={null}
                                            value={tradeDate}
                                        />}
                                </div>
                                <div className="col-1 d-flex align-items-center">
                                    <FormLabel>Date Added</FormLabel>
                                </div>
                                <div className="col">
                                    {isFetching
                                        ? <InputSkeleton />
                                        : <DateInput
                                            readOnly
                                            ButtonComponent={null}
                                            value={dateAdded}
                                        />}
                                </div>
                            </div>
                        </div>
                        <div className="col-4">
                            {isFetching
                                ? <InputSkeleton count={6} />
                                : <>
                                    <div className="row mb-2">
                                        <div className="col">
                                            <Label>Custodian:</Label>
                                            <span>{custodianCode}</span>
                                        </div>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col">
                                            <Label>WealthPro Account:</Label>
                                            <span>{wealthProAccountReference}</span>
                                        </div>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col">
                                            <Label>Status:</Label>
                                            <ThemedSpan variant="danger">{wealthProStatusDescription}</ThemedSpan>
                                        </div>
                                        <div className="col">
                                            <Label>Revision Marker:</Label>
                                            <span>{revisionMarker ?? "N/a"}</span>
                                        </div>
                                    </div>
                                    <div className="row mb-2">
                                        <div className="col">
                                            <Label>Transaction ID:</Label>
                                            <span>{platformTransactionId}</span>

                                        </div>
                                    </div>
                                </>}
                        </div>
                    </div>
    );
 

    return (
        <React.Fragment>
            <StyledCard className={"mb-3"}>
                <CardHeader className="d-flex align-items-center">
                    <span>Exceptions</span>
                    <div className="d-flex ms-auto">
                        <FormInput
                                placeholder="Search..."
                                disableAnimations
                                value={null}
                                onBlur={value => setSearch(value)}
                                className="me-3"
                                clearable
                            />
                        <Tooltip position={"left"} tooltip={"Amend Hubwise"}>
                            <IconButton className="me-3" icon="pen-to-square" onClick={() => setShowHubwiseTransactionsModal(true)} disabled={isFetching} />
                        </Tooltip>
                        <Tooltip position={"left"} tooltip={"Resolve multiple"}>
                            <IconButton className="me-3" icon="layer-group" onClick={() => setShowBulkModal(true)} disabled={isFetching} />
                        </Tooltip>
                        <Tooltip position={"left"} tooltip={"Re-match transactions"}>
                            <IconButton icon="refresh" onClick={() => setShowRetryModal(true)} disabled={isFetching} />
                        </Tooltip>
                    </div>
                </CardHeader>
                <CardBody>
                    {exception && body}
                </CardBody>
                <CardFooter>
                    <div className={classNames(
                        "py-1",
                        "d-flex flex-row align-items-center justify-content-between"
                    )}>
                        <div className="d-flex">
                            <IconButton
                                className="me-1"
                                icon="fa-backward-fast"
                                onClick={first}
                                disabled={isFetching}
                            />
                            <IconButton
                                className="me-2"
                                icon="fa-backward-step"
                                onClick={previous}
                                disabled={isFetching}
                            />
                            <IconButton
                                className="me-1"
                                icon="fa-forward-step"
                                onClick={next}
                                disabled={isFetching}
                            />
                            <IconButton
                                className="me-2"
                                icon="fa-forward-fast"
                                onClick={last}
                                disabled={isFetching}
                            />
                            <div className="d-flex align-items-center justify-content-start">
                                {!isFetching && <>
                                    <span>Transaction</span>
                                    <div className="w-25 mx-1">
                                        <NumberInput
                                            decimalScale={0}
                                            className="rounded text-end"
                                            value={page}
                                            onBlur={value => setPage(value)}
                                            isAllowed={values => {
                                                const { floatValue } = values;
                                                return floatValue > 0 && floatValue <= totalCount;
                                            }}
                                        />
                                    </div>
                                    <span>of {totalCount}</span>
                                </>}
                            </div>
                        </div>

                        {(!isFetching && exception) && <TransactionExceptionsResolveDropdown platformTransactionId={platformTransactionId} platformTransactionType={platformTransactionType} />}
                    </div>
                </CardFooter>
            </StyledCard>
            <BulkResolveModal
                show={showBulkModal}
                onHide={() => setShowBulkModal(false)}
            />
            <RetryMatchingModal
                show={showRetryModal}
                onClose={() => setShowRetryModal(false)}
            />
            <HubwiseTransactionsModal
                show={showHubwiseTransactionsModal}
                onHide={() => setShowHubwiseTransactionsModal(false)}
            />
        </React.Fragment>
    );

};

export default TransactionExceptions;